import { useMemo } from 'react';
import { influencer } from '@round/api';
import { Combobox, ComboboxCountryItem, ComboboxCountryItemType } from '@round/creatorbase-ui';
import { useCountries } from '@round/creatorbase-helpers/api-hooks';
import styles from './OutreachResultsLocationFilter.module.css';

// A second country already overflows the control, so past one they read as a count instead.
const MAX_VISIBLE_CHIPS = 1;

type CountryOption = ComboboxCountryItemType<number>;

type Props = {
    /** Comma separated `Location` ids, as the endpoint takes them. */
    value: string | undefined;
    onChange: (location: string | undefined) => void;
};

export const OutreachResultsLocationFilter = ({ value, onChange }: Props) => {
    const { countries, isLoading } = useCountries();

    const options = useMemo(() => countries.map(getCountryOption), [countries]);

    // A country the lookup has not arrived with yet still has to hold its place in the selection.
    const selectedOptions = useMemo(() => {
        const optionsById = new Map(options.map((option) => [option.value, option]));
        return parseLocationIds(value).map((id) => optionsById.get(id) ?? { value: id, label: String(id) });
    }, [options, value]);

    return (
        <div className={styles.container}>
            <Combobox<CountryOption, true>
                multiple
                disabled={isLoading}
                items={options}
                value={selectedOptions}
                maxVisibleChips={MAX_VISIBLE_CHIPS}
                placeholder="Any location"
                noItemsFound="No countries found"
                isItemEqualToValue={(option, selected) => option.value === selected.value}
                onValueChange={(selected) =>
                    onChange(selected.length ? selected.map((option) => option.value).join(',') : undefined)
                }
                renderItem={(item) => <ComboboxCountryItem item={item} />}
                isClearable
            />
        </div>
    );
};

const getCountryOption = (location: influencer.InfluencerLocation): CountryOption => ({
    value: location.id,
    label: location.country_name,
    countryCode: location.country_code,
});

const parseLocationIds = (value: string | undefined): number[] =>
    value
        ?.split(',')
        .map(Number)
        .filter((id) => !Number.isNaN(id)) ?? [];
